// The answer of one question screen, by the lib's question kind: a single
// choice, a multiple choice with its exclusive options, a 0–10 scale
// (SPEC-29's ScaleSelector), a stepper,
// a time of day, a written answer (LEAF-Q's « Comentários »), and the
// fields the lib draws under a chosen option (SPEC-21; LEAF-Q adds a
// number and a multiple choice there). The lib's item list says which
// options exist, which stand alone and which field shows under which
// option; the copy says their words. Nothing here judges an answer: the
// flow asks the lib.

import { Fragment } from "react";
import type { ReactNode } from "react";
import type { FieldValues, Question } from "poweryourflow-lib";

import { Field } from "../components/Field";
import { copy } from "../i18n/pt-PT";
import type { QuestionText } from "../i18n/pt-PT";
import { LIMITS } from "../schema/types";
import type { AnswerValue } from "../schema/types";
import { ScaleSelector } from "./ScaleSelector";
import type { ScaleAccent } from "./ScaleSelector";
import { Stepper } from "./Stepper";
import { TimeInput } from "./TimeInput";

const hints = copy.questionnaire.hints;

/** The kinds this engine renders; a card that needs another adds it here. */
export const RENDERED_KINDS: readonly Question["kind"][] = ["single", "multi", "scale", "number", "time", "text"];

/** The kinds a field under an option can be. */
export const RENDERED_FIELD_KINDS: readonly Question["kind"][] = ["text", "number", "multi"];

/** A screen's fields, as the lib's fieldsOf returns them, with their words and values. */
export interface OptionFields {
  questions: readonly Question[];
  text: (fieldId: string) => QuestionText | undefined;
  values: FieldValues;
  onChange: (fieldId: string, value: AnswerValue) => void;
}

/** The fields the lib shows under one option: those whose condition lists it. */
function FieldsUnder({ option, fields }: { option: string; fields: OptionFields | undefined }) {
  if (!fields) return null;
  const under = fields.questions.filter((field) => field.showIf?.anyOf?.includes(option));
  if (under.length === 0) return null;
  return (
    <div className="flex flex-col gap-3 pl-5">
      {under.map((field) => {
        const said = fields.text(field.id);
        if (!said) return null;
        const value = fields.values[field.id];
        const onChange = (next: AnswerValue) => fields.onChange(field.id, next);
        switch (field.kind) {
          case "text":
            return (
              <Field
                key={field.id}
                label={said.title}
                error={null}
                {...(field.optional && { hint: copy.questionnaire.optionalField })}
                {...(said.placeholder !== undefined && { placeholder: said.placeholder })}
                {...(field.maxLength !== undefined && { maxLength: field.maxLength })}
                value={typeof value === "string" ? value : ""}
                onChange={(event) => onChange(event.target.value)}
              />
            );
          case "number":
            return (
              <Stepper
                key={field.id}
                question={field}
                label={said.title}
                value={typeof value === "number" ? value : undefined}
                unit={said.unit}
                onChange={onChange}
              />
            );
          case "multi":
            return (
              <fieldset key={field.id} className="flex flex-col gap-3">
                <legend className="pb-2 text-body-sm font-semibold text-dark">{said.title}</legend>
                <Checkboxes question={field} text={said} value={value} onChange={onChange} />
              </fieldset>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

const optionCard =
  "flex min-h-[var(--control-h)] w-full items-center justify-between gap-4 rounded-card-inner border-[1.5px] bg-white px-5 py-4 " +
  "text-left text-body font-medium shadow-option transition-colors duration-[var(--dur-micro)] ease-pyf";

/** A radio or a check fills over the same 120 ms as its card's border. */
const marker = "transition-colors duration-[var(--dur-micro)] ease-pyf";

const chosenCard = (chosen: boolean) => `${optionCard} ${chosen ? "border-violet" : "border-transparent"}`;

function Radio({ chosen }: { chosen: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex size-6 shrink-0 items-center justify-center rounded-pill border-[1.5px] ${marker} ${chosen ? "border-violet bg-violet" : "border-line-25"}`}
    >
      {chosen && <span className="size-2 rounded-pill bg-white" />}
    </span>
  );
}

function Check({ chosen }: { chosen: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex size-6 shrink-0 items-center justify-center rounded-check border-[1.5px] text-caption font-semibold ${marker} ${chosen ? "border-violet bg-violet text-white" : "border-line-25"}`}
    >
      {chosen && "✓"}
    </span>
  );
}

function OptionButton({
  role,
  chosen,
  onClick,
  children,
}: {
  role: "radio" | "checkbox";
  chosen: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" role={role} aria-checked={chosen} onClick={onClick} className={chosenCard(chosen)}>
      <span>{children}</span>
      {role === "radio" ? <Radio chosen={chosen} /> : <Check chosen={chosen} />}
    </button>
  );
}

/**
 * A multiple choice after a tap: an exclusive option ("nunca") stands
 * alone, and any other option clears it.
 */
function toggled(question: Question, chosen: readonly string[], option: string): string[] {
  if (chosen.includes(option)) return chosen.filter((item) => item !== option);
  const exclusive = question.exclusive ?? [];
  if (exclusive.includes(option)) return [option];
  return [...chosen.filter((item) => !exclusive.includes(item)), option];
}

/** The options of a multiple choice, each followed by the fields the lib draws under it. */
function Checkboxes({
  question,
  text,
  value,
  onChange,
  fields,
}: {
  question: Question;
  text: QuestionText;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue) => void;
  fields?: OptionFields | undefined;
}) {
  const chosenList = Array.isArray(value) ? (value as readonly string[]) : [];
  return (
    <>
      {(question.options ?? []).map((option) => (
        <Fragment key={option}>
          <OptionButton
            role="checkbox"
            chosen={chosenList.includes(option)}
            onClick={() => onChange(toggled(question, chosenList, option))}
          >
            {text.options?.[option] ?? option}
          </OptionButton>
          <FieldsUnder option={option} fields={fields} />
        </Fragment>
      ))}
    </>
  );
}

export function AnswerInput({
  question,
  text,
  value,
  onChange,
  fields,
  accent = "violet",
}: {
  question: Question;
  text: QuestionText;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue) => void;
  fields?: OptionFields;
  accent?: ScaleAccent;
}) {
  const options = question.options ?? [];
  const label = (option: string) => text.options?.[option] ?? option;

  switch (question.kind) {
    case "single":
      return (
        <fieldset className="flex flex-col gap-3">
          <legend className="pb-3 text-body-sm text-muted">{hints.single}</legend>
          {options.map((option) => (
            <Fragment key={option}>
              <OptionButton role="radio" chosen={value === option} onClick={() => onChange(option)}>
                {label(option)}
              </OptionButton>
              <FieldsUnder option={option} fields={fields} />
            </Fragment>
          ))}
        </fieldset>
      );

    case "multi":
      return (
        <fieldset className="flex flex-col gap-3">
          <legend className="pb-3 text-body-sm text-muted">{hints.multi}</legend>
          <Checkboxes question={question} text={text} value={value} onChange={onChange} fields={fields} />
        </fieldset>
      );

    case "scale":
      return (
        <ScaleSelector
          min={question.min ?? 0}
          max={question.max ?? 0}
          value={typeof value === "number" ? value : undefined}
          onChange={onChange}
          label={text.title}
          ends={text.scale}
          accent={accent}
        />
      );

    case "number":
      return <Stepper question={question} value={typeof value === "number" ? value : undefined} unit={text.unit} onChange={onChange} />;

    case "time":
      return <TimeInput question={question} value={typeof value === "string" ? value : undefined} onChange={onChange} />;

    case "text": {
      const max = Math.min(question.maxLength ?? LIMITS.TEXT_ANSWER_MAX, LIMITS.TEXT_ANSWER_MAX);
      const hint = [question.optional ? copy.questionnaire.optionalField : "", hints.text(max)].filter(Boolean).join(" ");
      return (
        <label className="flex flex-col gap-3">
          <span className="text-body-sm text-muted">{hint}</span>
          <textarea
            aria-label={text.title}
            rows={5}
            maxLength={max}
            value={typeof value === "string" ? value : ""}
            onChange={(event) => onChange(event.target.value)}
            className={
              "w-full resize-none rounded-card-inner border-[1.5px] border-line-20 bg-white px-5 py-4 text-body-lg text-dark " +
              "outline-none transition-colors duration-[var(--dur-micro)] ease-pyf focus:border-violet"
            }
          />
        </label>
      );
    }

    default:
      return null;
  }
}
